import type { DesignatedVendor } from "@/features/partners/api";
import type {
  DrawingMatchOutcome,
  OcrConfidence,
  OcrStatus,
  ThumbnailStatus,
} from "@/features/drawings/api";
import { PDF_CONTENT_TYPE } from "@/features/drawings/api";
import { api } from "@/lib/api";
import type { Pagination } from "@/lib/types";

export type { Pagination };

/** 見積の状態。API の QuotationStatus（lib/enums/quotation_status.py）と一致させる。 */
export type QuotationStatus =
  "requested" | "draft" | "submitted" | "ordered" | "lost" | "declined";

/**
 * 状態の表示名。
 *
 * 「辞退」は見積を出さずに断った場合で、「失注」（出したが取れなかった）とは
 * 別に数える。受注率の母数が変わるため。
 *
 * 一覧の絞り込みと詳細の状態変更は、どちらもこの定義から選択肢を組み立てる。
 */
export const QUOTATION_STATUS_LABEL: Record<QuotationStatus, string> = {
  requested: "依頼受領",
  draft: "作成中",
  submitted: "提出済",
  ordered: "受注",
  lost: "失注",
  declined: "辞退",
};

/** 一覧・詳細で返る見積（API の QuotationResponse に対応）。 */
export interface Quotation {
  quotation_id: number;
  quotation_number: string;
  client_id: number | null;
  client_name: string | null;
  status: QuotationStatus;
  sales_rep_id: string | null;
  quoted_by: string | null;
  requested_at: string | null;
  quoted_date: string | null;
  valid_until: string | null;
  lost_reason: string | null;
  /** 備考（客先向け。見積書に載る）。 */
  note: string | null;
  /** 社内備考（客先には出さない）。 */
  internal_note: string | null;
  /** 客先の見積依頼番号（京セラの購番、平田機工の見積依頼番号）。 */
  client_request_number: string | null;
  subject: string | null;
  /**
   * セット品か。**同一のセットを複数セット作る注文**。受注のとき案件へ写す。
   * 畳んだ明細の数量は購番をまたいだ合算になる。
   */
  is_set_product: boolean;
  /** セット数。セット品でなければ null。 */
  set_count: number | null;
  /**
   * この見積の明細に付いている購番（重複なし・昇順）。
   * `client_request_number` は 1 本しか持てないので、複数本はこちらに入る。
   */
  client_order_numbers: string[];
  /** 客先への回答期限。valid_until（提出した見積の有効期限）とは別物。 */
  answer_due_date: string | null;
  /**
   * 回答納期（客先に回答した納入日）。**明細から導く値で、見積ヘッダは持たない**。
   *
   * 明細の回答納期のうち最も遅い日が入る（見積は全行揃って納まるため）。行の
   * 回答納期が空なら客先の希望どおりで回答したという意味なので、その行は希望
   * 納期で数える。除外した明細は見ない。直すのは明細の側。
   */
  answered_deadline: string | null;
  /** 特急（客先の緊急区分）。状態とは別軸で立つ。 */
  is_urgent: boolean;
  client_request_date: string | null;
  ship_to: string | null;
  client_department: string | null;
  client_contact_name: string | null;
  material_supply_date: string | null;
  /** 通貨（ISO 4217）。現状は JPY のみ。 */
  currency: string;
  payment_terms: string | null;
  item_count: number;
  /**
   * 見積完了になっている明細の数。一覧の進捗ゲージの分子で、分母は `item_count`。
   *
   * 運搬費や値引きの行も見積完了にできるので、母数は図面行には絞らない。
   * 除外した明細は分母（`item_count`）にも分子にも入らない。
   */
  completed_item_count: number;
  /**
   * 金額は API が NUMERIC を文字列で返す（浮動小数の誤差を持ち込まないため）。
   * 除外した明細は含まない。
   */
  total_amount: string;
  /**
   * この見積から起票された案件の id / 工番（無ければ null）。
   *
   * 受注を取り消すと消える案件を、取り消す前に確認ダイアログへ出すために返る。
   * **詳細（取得・更新）でのみ入る**。一覧では常に null。
   */
  ordered_project_id: number | null;
  ordered_project_number: string | null;
  /**
   * この見積を起こした案件（特急フロー＝案件が先、見積が後）。
   *
   * **中身の対応は持たない**（それは案件明細の `quotation_item_id`）。受注に
   * したときに案件を作るのか、この案件に結び付けるのかを決める
   * （docs/urgent-project-flow.md）。**詳細でのみ入る**。
   */
  origin_project_id: number | null;
  origin_project_number: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuotationListResponse {
  items: Quotation[];
  pagination: Pagination;
}

export interface QuotationListParams {
  keyword?: string;
  status?: QuotationStatus;
  clientId?: number;
  sortKey?: string;
  ascending?: boolean;
  limit: number;
  offset: number;
}

export function listQuotations(
  params: QuotationListParams,
): Promise<QuotationListResponse> {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.status) query.set("status", params.status);
  if (params.clientId != null) query.set("client_id", String(params.clientId));
  if (params.sortKey) query.set("sort_key", params.sortKey);
  if (params.ascending != null) query.set("ascending", String(params.ascending));
  return api.get<QuotationListResponse>(`/quotations?${query}`);
}

/** 見積ヘッダの更新で送れる項目。 */
export type QuotationInput = Partial<
  Pick<
    Quotation,
    | "client_id"
    | "status"
    | "sales_rep_id"
    | "quoted_by"
    | "requested_at"
    | "quoted_date"
    | "valid_until"
    | "lost_reason"
    | "note"
    | "internal_note"
    | "client_request_number"
    | "subject"
    | "is_set_product"
    | "set_count"
    | "answer_due_date"
    | "is_urgent"
    | "client_request_date"
    | "ship_to"
    | "client_department"
    | "client_contact_name"
    | "material_supply_date"
    | "currency"
    | "payment_terms"
  >
>;

/**
 * 見積を起票する。
 *
 * 見積番号は API が採番する。`input` を省くと番号だけが採番された空の見積に
 * なるが、画面からは**登録ダイアログで入力させてから**呼ぶ。押した瞬間に
 * 番号を消費すると、取りやめたときに中身の無い見積が残るため。
 */
export function createQuotation(input: QuotationInput = {}): Promise<Quotation> {
  return api.post<Quotation>("/quotations", input);
}

/** 見積を 1 件取得する。 */
export function getQuotation(quotationId: number): Promise<Quotation> {
  return api.get<Quotation>(`/quotations/${quotationId}`);
}

/** 見積ヘッダを部分更新する。渡した項目だけ反映される。 */
export function updateQuotation(
  quotationId: number,
  input: QuotationInput,
): Promise<Quotation> {
  return api.patch<Quotation>(`/quotations/${quotationId}`, input);
}

/**
 * セット品を切り替える。**見積と案件の明細の形も一緒に変わる**ので、見積の更新
 * とは別の口（[[set_product_fold]]）。
 *
 * true で同じ部品の明細を畳んで数量を合算し、false で客先購番ごとの行に分け直す。
 * 分けた行には代表行の工程別見積を複製するので、**合計金額は変わらない**。
 */
export function setQuotationSetProduct(
  quotationId: number,
  isSetProduct: boolean,
): Promise<Quotation> {
  return api.post<Quotation>(`/quotations/${quotationId}/set-product`, {
    is_set_product: isSetProduct,
  });
}

export function deleteQuotation(quotationId: number): Promise<void> {
  return api.delete<void>(`/quotations/${quotationId}`);
}

/**
 * 受注を取り消し、起票した案件を削除して見積の状態を「提出済」に戻す。
 *
 * 打ち間違いの巻き戻し。案件を残すと二重起票の判定に引っかかって受注を
 * やり直せないので、案件ごと消す（図面の実体は消えない）。出荷済みの案件が
 * あるときは 409 で断られる。
 */
export function cancelQuotationOrder(quotationId: number): Promise<Quotation> {
  return api.delete<Quotation>(`/quotations/${quotationId}/order`);
}

/**
 * 選んだ見積を明細ごとまとめて削除する。
 *
 * **1 件でも消せなければ何も消えない**（案件に紐付いた見積は 409 で、どの見積か
 * が見積番号で返る）。DELETE ではなく POST なのは、消す対象を本文で渡すため。
 */
export function deleteQuotations(quotationIds: number[]): Promise<void> {
  return api.post<void>("/quotations/delete", { quotation_ids: quotationIds });
}

/** 結び付けたときに結ばれる 1 組（特急フロー）。 */
export interface AttachMatchedRow {
  drawing_number: string;
  quotation_item_id: number;
  project_item_id: number;
  quotation_quantity: number;
  project_quantity: number;
  /**
   * 数量が食い違う。**結ぶのは止めない**（実際に作っている数＝案件が正で、
   * 直すのは見積の側）ので、押す前に気づけるようにするための印。
   */
  quantity_differs: boolean;
}

/** 結ばれずに残る 1 行と、その理由。 */
export interface AttachUnmatchedRow {
  drawing_number: string;
  item_id: number;
  item_name: string;
  /** `ambiguous`=同じ図番が相手側に複数ある、`no_match`=相手がいない。 */
  reason: "ambiguous" | "no_match";
}

/** 結び付けたら何が起きるか（docs/urgent-project-flow.md）。 */
export interface AttachPreview {
  project_id: number;
  project_number: string;
  matched: AttachMatchedRow[];
  unmatched_quotation_items: AttachUnmatchedRow[];
  unmatched_project_items: AttachUnmatchedRow[];
}

/**
 * 結び付けたときの内訳を、**保存せずに**取る。
 *
 * 結び付けると見積は受注済みになって直せなくなるので、押す前に見せる。実行と
 * 同じ計画を通すので、見せた内容と結果は食い違わない。
 */
export function previewQuotationOrder(quotationId: number): Promise<AttachPreview> {
  return api.get<AttachPreview>(`/quotations/${quotationId}/order/preview`);
}

/** 見積明細（API の QuotationItemResponse に対応）。 */
export interface QuotationItem {
  quotation_item_id: number;
  quotation_id: number;
  sequence: number;
  drawing_id: number | null;
  /** 製作対象でない行（運搬費・値引きなど）では null。 */
  drawing_number: string | null;
  /** 図面行は図面の品名、それ以外は明細に入力された品名。 */
  item_name: string;
  quantity: number;
  /**
   * 単価。図面行は工程別見積の合算、それ以外の行は明細に入力された単価。
   * 金額は API が NUMERIC を文字列で返す（浮動小数の誤差を持ち込まないため）。
   */
  unit_price: string;
  amount: string;
  /** 図面行は true。単価の出どころが工程なので、この列は直接編集できない。 */
  priced_by_processes: boolean;
  /** 工程の内訳行数。図面行のみ。0 なら工程がまだ入っていない。 */
  process_count: number;
  /** 摘要（客先向け）。 */
  note: string | null;
  /** 社内備考（客先には出さない）。 */
  internal_note: string | null;
  /** 客先からの質問（依頼ファイルの「質問a」）。 */
  client_question: string | null;
  /** 質問への回答。回答ファイルの「質問aへの回答」に書き戻される。 */
  question_answer: string | null;
  /**
   * この行の見積が済んだか。画面のステータスは「未入力（工程なし）」
   * 「入力中（工程あり）」「完了（このフラグ）」の 3 段階で見せる。
   */
  completed: boolean;
  /**
   * 見積から外した行。客先が明細の一部だけを発注してきたときに立てる。
   *
   * 行は消さずに残すので、何を見積って何が落ちたのかが後から辿れる。除外した行は
   * 合計金額（`total_amount`）と進捗の集計から外れ、受注しても案件明細に
   * コピーされない。**中身も直せない**（API が 400 を返す）ので、画面ではセルを
   * 編集不可にしてグレーで見せる。
   */
  cancelled: boolean;
  client_item_code: string | null;
  client_unit_no: string | null;
  client_bom_no: string | null;
  /**
   * この行に畳み込んだ客先購番（重複なし・昇順）。セット品はセット数と同じ
   * 本数が並ぶ。**直せない**（紙の行そのものの記録）。
   */
  client_order_numbers: string[];
  /** 単位（PC など客先の表記）。 */
  unit: string | null;
  requested_deadline: string | null;
  /** 回答納期。null なら希望納期どおりで回答したことを表す。 */
  answered_deadline: string | null;
  /** 回答数量。null なら希望数量（quantity）どおり。 */
  answered_quantity: number | null;
  /** 客先の目標単価。自社の見積単価ではないので金額計算には使わない。 */
  target_unit_price: string | null;
  maker_name: string | null;
  maker_model: string | null;
  /** 材質。**図面に属する情報**なので明細からは変更できない。 */
  material: string | null;
  /** 表面処理。**図面に属する情報**なので明細からは変更できない。 */
  surface_treatment: string | null;
  /** 依頼ファイルに書かれていた図番の原文。図面に属する情報。 */
  client_drawing_number_raw: string | null;
  /** 客先の図面番号（平田機工の「図面番号」）。図番とは別の番号。 */
  client_drawing_sheet_no: string | null;
  /** 図面 PDF の版数。図面行のみ。0 なら PDF が 1 件も登録されていない。 */
  drawing_revision_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuotationItemListResponse {
  items: QuotationItem[];
  /** 各行の金額の合計（値引き行は負値）。**除外した行は含まない**。 */
  total_amount: string;
}

export function listQuotationItems(
  quotationId: number,
): Promise<QuotationItemListResponse> {
  return api.get<QuotationItemListResponse>(`/quotations/${quotationId}/items`);
}

/** 明細の追加・更新で送れる項目。 */
export interface QuotationItemInput {
  /**
   * 図番。入っている行が製作対象。
   *
   * **行の種類は起票時に決まる**ので、更新では製作対象でない行に入れることも、
   * 製作対象の行で空にすることもできない（API が 400 を返す）。
   */
  drawing_number?: string | null;
  item_name?: string | null;
  material?: string | null;
  surface_treatment?: string | null;
  /** 依頼ファイルに書かれていた図番の原文。 */
  client_drawing_number_raw?: string | null;
  /** 客先の図面番号（平田機工の「図面番号」）。 */
  client_drawing_sheet_no?: string | null;
  quantity?: number;
  /**
   * 単価。製作対象でない行でのみ使う（値引き行は負値）。製作対象の行の単価は
   * 工程別見積の合算なので、送っても API に無視される。
   * 文字列で送って浮動小数の誤差を避ける。
   */
  unit_price?: string;
  note?: string | null;
  internal_note?: string | null;
  /** 客先からの質問（依頼ファイルの「質問a」）。 */
  client_question?: string | null;
  /** 質問への回答。回答ファイルに書き戻される。 */
  question_answer?: string | null;
  completed?: boolean;
  /**
   * 除外するか。**除外中の行は他の項目を送れない**（解除と同時に送るのも 400）。
   * まとめて除外するときは `cancelQuotationItems`。
   */
  cancelled?: boolean;
  client_item_code?: string | null;
  client_unit_no?: string | null;
  client_bom_no?: string | null;
  unit?: string | null;
  requested_deadline?: string | null;
  answered_deadline?: string | null;
  answered_quantity?: number | null;
  /** 文字列で送って浮動小数の誤差を避ける（単価と同じ）。 */
  target_unit_price?: string | null;
  maker_name?: string | null;
  maker_model?: string | null;
}

export function createQuotationItem(
  quotationId: number,
  input: QuotationItemInput,
): Promise<QuotationItem> {
  return api.post<QuotationItem>(`/quotations/${quotationId}/items`, input);
}

export function updateQuotationItem(
  quotationId: number,
  itemId: number,
  input: QuotationItemInput,
): Promise<QuotationItem> {
  return api.patch<QuotationItem>(`/quotations/${quotationId}/items/${itemId}`, input);
}

export function deleteQuotationItem(
  quotationId: number,
  itemId: number,
): Promise<void> {
  return api.delete<void>(`/quotations/${quotationId}/items/${itemId}`);
}

/**
 * 選んだ明細をまとめて削除し、削除後の一覧を返す。
 *
 * **1 件でも消せなければ何も消えない**（案件に紐付いた行は 409）。DELETE では
 * なく POST なのは、消す対象を本文で渡すため。
 */
export function deleteQuotationItems(
  quotationId: number,
  itemIds: number[],
): Promise<QuotationItemListResponse> {
  return api.post<QuotationItemListResponse>(
    `/quotations/${quotationId}/items/delete`,
    {
      quotation_item_ids: itemIds,
    },
  );
}

/**
 * 選んだ明細を見積から外す（`cancelled` を false にすると解除）。
 *
 * 客先が明細の一部だけを発注してきたときに使う。**1 件でも外せなければ何も
 * 変わらない**（案件に紐付いた行は 409）。削除と同じく POST なのは、対象を
 * 本文で渡すため。
 */
export function cancelQuotationItems(
  quotationId: number,
  itemIds: number[],
  cancelled: boolean,
): Promise<QuotationItemListResponse> {
  return api.post<QuotationItemListResponse>(
    `/quotations/${quotationId}/items/cancel`,
    {
      quotation_item_ids: itemIds,
      cancelled,
    },
  );
}

/** 明細の行番号を、渡した並びで 1..N に振り直す（全明細を過不足なく渡す）。 */
export function reorderQuotationItems(
  quotationId: number,
  itemIds: number[],
): Promise<QuotationItemListResponse> {
  return api.put<QuotationItemListResponse>(`/quotations/${quotationId}/items/order`, {
    quotation_item_ids: itemIds,
  });
}

export interface QuotationImportHeaderPreview {
  client_request_number: string | null;
  subject: string | null;
  answer_due_date: string | null;
  is_urgent: boolean;
  client_request_date: string | null;
  ship_to: string | null;
  client_department: string | null;
  client_contact_name: string | null;
  material_supply_date: string | null;
  currency: string | null;
  payment_terms: string | null;
  valid_until: string | null;
  quoted_date: string | null;
}

export interface QuotationImportItemPreview {
  /** ファイルの行番号（ヘッダ行を 1 とする）。 */
  row: number;
  /** 図番。ファイルに書かれていたまま取り込む。 */
  drawing_number: string | null;
  /** この行が属する客先依頼番号（1 ファイルに複数件入っている場合の振り分け先）。 */
  client_request_number: string | null;
  item_name: string | null;
  quantity: number;
  unit: string | null;
  requested_deadline: string | null;
}

export interface QuotationImportWarning {
  /** 行を特定できないものは null。 */
  row: number | null;
  message: string;
}

/**
 * 依頼 1 件ぶん（客先依頼番号が同じ行のまとまり）＝見積 1 件ぶん。
 *
 * 1 ファイルに複数件の依頼が入っていることがあるので、依頼ごとに分けて返る。
 * 1 件しか入っていなければ 1 つだけになる。
 */
export interface QuotationImportGroup {
  client_request_number: string | null;
  item_count: number;
  header: QuotationImportHeaderPreview;
  /** 同じ客先依頼番号で起票済みの見積番号（二重取り込みに気づくため）。 */
  existing_quotation_numbers: string[];
}

export interface QuotationImportResult {
  vendor: DesignatedVendor;
  /** 指定業者から決まる得意先。取り込みで得意先を選ばせないための経路。 */
  client_id: number;
  client_name: string;
  /** true なら何も保存していない（取り込み前の下見）。 */
  dry_run: boolean;
  /** 全依頼ぶんの合計行数。 */
  item_count: number;
  /** 依頼ごとのまとまり。2 つ以上なら分割するか原本を直すかを選んでもらう。 */
  groups: QuotationImportGroup[];
  items: QuotationImportItemPreview[];
  warnings: QuotationImportWarning[];
  /** 下見のときは空。分割して取り込んだ場合は依頼番号ごとに並ぶ。 */
  quotations: Quotation[];
}

/**
 * 見積依頼の Excel を取り込む。得意先は指定業者から決まるので渡さない。
 *
 * `dryRun` で下見（解析結果だけ返る）と実行を切り替える。どちらも同じ解析を
 * 通るので、下見で見えた内容がそのまま起票される。
 *
 * 依頼番号が複数あるファイルは `split` を付けないとサーバが 400 で止める。
 * 原本の誤りの可能性があるため、分けて登録する意思を明示してもらう。
 */
export function importQuotation(params: {
  file: File;
  vendor: DesignatedVendor;
  dryRun: boolean;
  split?: boolean;
}): Promise<QuotationImportResult> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("vendor", params.vendor);
  form.append("dry_run", String(params.dryRun));
  form.append("split", String(params.split ?? false));
  return api.postForm<QuotationImportResult>("/quotations/import", form);
}

/**
 * 見積明細の工程別内訳（API の QuotationProcessResponse に対応）。
 *
 * 図面行の単価はこの内訳の合算で決まる。明細側に単価を持たせていないのは、
 * 工程を直したのに単価が古いままという二重管理を避けるため。
 */
export interface QuotationProcess {
  quotation_process_id: number;
  quotation_item_id: number;
  sequence: number;
  process_id: number;
  process_code: string;
  process_name: string;
  /** 使用停止になった工程。過去の見積はそのまま残るので true になりうる。 */
  process_disabled: boolean;
  /** 所要時間（時間単位。0.25 = 15 分）。金額を直接入力した行では null。 */
  hours: string | null;
  /** 見積った時点の時間単価の控え。工程マスタを変えてもここは動かない。 */
  hourly_price: string | null;
  /** 金額（1 個あたり）。金額は API が NUMERIC を文字列で返す。 */
  price: string;
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuotationProcessListResponse {
  items: QuotationProcess[];
  /** 合算＝この明細の単価。明細の金額はこれ × 数量。 */
  total_price: string;
}

/**
 * 内訳の追加・更新で送れる項目。
 *
 * 金額の入れ方は 2 通り。`hours` を入れると 時間 × 時間単価 で決まり、
 * `hours` を空にすると `price` を直接使う。
 */
export interface QuotationProcessInput {
  process_id?: number;
  hours?: string | null;
  hourly_price?: string | null;
  price?: string | null;
  note?: string | null;
}

const processesPath = (quotationId: number, itemId: number) =>
  `/quotations/${quotationId}/items/${itemId}/processes`;

export function listQuotationProcesses(
  quotationId: number,
  itemId: number,
): Promise<QuotationProcessListResponse> {
  return api.get<QuotationProcessListResponse>(processesPath(quotationId, itemId));
}

export function createQuotationProcess(
  quotationId: number,
  itemId: number,
  input: QuotationProcessInput,
): Promise<QuotationProcess> {
  return api.post<QuotationProcess>(processesPath(quotationId, itemId), input);
}

export function updateQuotationProcess(
  quotationId: number,
  itemId: number,
  processRowId: number,
  input: QuotationProcessInput,
): Promise<QuotationProcess> {
  return api.patch<QuotationProcess>(
    `${processesPath(quotationId, itemId)}/${processRowId}`,
    input,
  );
}

export function deleteQuotationProcess(
  quotationId: number,
  itemId: number,
  processRowId: number,
): Promise<void> {
  return api.delete<void>(`${processesPath(quotationId, itemId)}/${processRowId}`);
}

/**
 * 他の見積明細から工程別内訳を写す（実績参照からの複製）。
 *
 * **写す先の内訳はいったん全部捨てられる**。足す形にすると同じ工程が 2 行並び、
 * 単価が写した見積の倍になるため。捨てていいかの確認は呼ぶ側で取る。
 */
export function copyQuotationProcesses(
  quotationId: number,
  itemId: number,
  sourceQuotationItemId: number,
): Promise<QuotationProcessListResponse> {
  return api.post<QuotationProcessListResponse>(
    `${processesPath(quotationId, itemId)}/copy`,
    { source_quotation_item_id: sourceQuotationItemId },
  );
}

// ------------------------------------------------------------------ 実績参照
/**
 * 同じ図番の過去案件 1 行（API の PastRecordResponse に対応）。
 *
 * 案件側（受注日・受注単価）と見積側（希望単価・回答単価）を 1 行に並べたもの。
 * 見積側は**案件明細が見積明細に紐付いていれば**入り、紐付いていなければ
 * すべて null になる（特急フローで起票しただけの案件）。
 */
export interface PastRecord {
  project_item_id: number;
  project_id: number;
  project_number: string;
  project_title: string | null;
  client_name: string | null;
  /** 受注日（案件全体）。紙に無いこともある。 */
  order_date: string | null;
  quantity: number;
  item_name: string;
  /** 受注単価（発注書に書かれていた単価）。自社の見積ではない。 */
  order_unit_price: string | null;
  /** 紐付いている見積明細。null なら見積が残っていない行。 */
  quotation_item_id: number | null;
  quotation_id: number | null;
  quotation_number: string | null;
  quotation_sequence: number | null;
  /** 希望単価（客先が提示してきた目標単価）。 */
  target_unit_price: string | null;
  /** 回答単価（自社が出した単価。図面行は工程別内訳の合算）。 */
  quoted_unit_price: string | null;
  /** 工程別内訳の行数。0 なら写す中身が無い。 */
  process_count: number;
  /** 見積明細に紐付いていて、工程別内訳がある行だけ true。 */
  copyable: boolean;
}

export interface PastRecordListResponse {
  items: PastRecord[];
}

/** この明細と同じ図番を持つ過去の案件明細を、受注日の新しい順に取得する。 */
export function listItemPastRecords(
  quotationId: number,
  itemId: number,
): Promise<PastRecordListResponse> {
  return api.get<PastRecordListResponse>(
    `/quotations/${quotationId}/items/${itemId}/past-records`,
  );
}

// ---------------------------------------------------------------- 図面タブ
// 図面まわりの共通の型とヘルパは [[features/drawings/api]] に置いてある
// （案件の図面タブと同じものを使うため）。ここから使う側のために通しておく。
export type {
  DrawingMatchOutcome,
  OcrConfidence,
  OcrStatus,
  ThumbnailStatus,
} from "@/features/drawings/api";
export { thumbnailSrc } from "@/features/drawings/api";

export interface QuotationDrawing {
  drawing_id: number;
  /**
   * この図面から読み取った図番。突き合わせの鍵になる。
   * **まだ読み取っていなければ null**（取り込んだ直後など）。
   */
  drawing_number: string | null;
  note: string | null;
  revision_count: number;
  latest_revision_id: number | null;
  /**
   * 最新の版の 1 ページ目のサムネイルを取りに来る **API のパス**。
   *
   * 以前は期限付きの S3 URL だったが、署名が毎回ちがうので値が変わり続け、
   * **一覧を取り直すたびにブラウザが全サムネイルを取り直していた**（図番が 1 件
   * 読めるたびに起きる）。固定のパスにしてキャッシュを効かせる。
   * `thumbnailSrc()` で `VITE_API_URL` を前に付けて使う。生成前・失敗時は null。
   */
  thumbnail_path: string | null;
  thumbnail_status: ThumbnailStatus | null;
  ocr_status: OcrStatus | null;
  /**
   * OCR が読み取った図番。**採用されたかどうかとは別の値**。
   *
   * 図番は空のときだけ採用するので、`drawing_number` と食い違っていれば
   * 「人の入力と OCR の読みが違う」ことを意味する。
   */
  ocr_drawing_number: string | null;
  ocr_confidence: OcrConfidence | null;
  /** 確度の根拠（2 位とのスコア差）。一覧には出さず、図面を開いたときの補足に使う。 */
  ocr_margin: number | null;
  /** 結び付いた明細。null なら未照合。 */
  quotation_item_id: number | null;
  item_sequence: number | null;
  item_name: string | null;
  /**
   * この図面を使っている案件の工番。**見積と案件は同じ 1 枚を共有する**ので、
   * 入っている図面は見積側からは削除できない（API が 409 を返す）。
   */
  project_number: string | null;
  /**
   * 最新の版が QR 押印済みか。
   *
   * QR は案件側で押すが、**図面の実体は見積と共有している**ので版はこちらにも
   * 現れる。版表記では分からないのでバッジで示す。
   */
  qr_stamped: boolean;
  created_at: string | null;
}

export interface QuotationDrawingListResponse {
  items: QuotationDrawing[];
  /** 明細に結び付いていない図面の数。 */
  unmatched_count: number;
}

export interface DrawingMatchResult {
  drawing_id: number;
  drawing_number: string | null;
  outcome: DrawingMatchOutcome;
  quotation_item_id: number | null;
  /** ambiguous のときの候補。 */
  candidate_item_ids: number[] | null;
}

export function listQuotationDrawings(
  quotationId: number,
): Promise<QuotationDrawingListResponse> {
  return api.get<QuotationDrawingListResponse>(`/quotations/${quotationId}/drawings`);
}

/** 見積に図面を 1 枚足す（明細には結び付けない）。図番だけの器を作るときに使う。 */
export function createQuotationDrawing(
  quotationId: number,
  drawingNumber: string | null,
): Promise<QuotationDrawing> {
  return api.post<QuotationDrawing>(`/quotations/${quotationId}/drawings`, {
    drawing_number: drawingNumber,
  });
}

export interface QuotationDrawingImportResponse extends QuotationDrawingListResponse {
  /** 作られた図面の数（＝ PDF のページ数）。 */
  created_count: number;
}

/**
 * 図面 PDF を取り込む。**ページごとに別の図面として登録される**。
 *
 * 客先は 1 ファイルに複数ページ分の図面を入れて送ってくる。1 ページ = 図面 1 枚
 * なので、分割はサーバー側でやる。1 ページの PDF なら図面は 1 枚。
 *
 * 図番はサーバーが PDF から読み取る（OCR）。読めなかったときの受け皿として
 * ファイル名からの推測が入るが、その判断もサーバー側なので、ここは元のファイル名
 * を渡すだけでよい。
 */
export async function importQuotationDrawings(
  quotationId: number,
  file: File,
): Promise<QuotationDrawingImportResponse> {
  const issued = await api.post<{ upload_url: string; s3_key: string }>(
    `/quotations/${quotationId}/drawings/upload-url`,
    { content_type: PDF_CONTENT_TYPE },
  );

  const res = await fetch(issued.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": PDF_CONTENT_TYPE },
  });
  if (!res.ok) {
    throw new Error(
      `PDF のアップロードに失敗しました（${res.status}）。もう一度お試しください。`,
    );
  }

  return api.post<QuotationDrawingImportResponse>(
    `/quotations/${quotationId}/drawings/import`,
    {
      s3_key: issued.s3_key,
      original_filename: file.name,
    },
  );
}

/**
 * 未照合の図面を図番で明細と突き合わせる。
 *
 * `drawingIds` を渡すとその図面だけが対象になる（画面でカードを選んでから実行）。
 * 省くと見積の図面すべてが対象。
 */
/**
 * 図番で明細と突き合わせる。
 *
 * `dryRun` を立てると**何も保存せず**、何が起きるかだけ返る。ダイアログが実行前に
 * 内訳を見せるのに使う。**判定はサーバーに任せる**（同じ図番の明細が複数ある、
 * 既に別の図面が付いている、といった規則を画面側に二重に持つと、見せた内容と
 * 実際の結果がずれる）。
 */
export function matchQuotationDrawings(
  quotationId: number,
  drawingIds?: number[],
  dryRun = false,
): Promise<{ results: DrawingMatchResult[] }> {
  return api.post<{ results: DrawingMatchResult[] }>(
    `/quotations/${quotationId}/drawings/match`,
    { drawing_ids: drawingIds ?? null, dry_run: dryRun },
  );
}

export function updateQuotationDrawing(
  quotationId: number,
  drawingId: number,
  input: { drawing_number?: string | null; note?: string | null },
): Promise<QuotationDrawingListResponse> {
  return api.patch<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/${drawingId}`,
    input,
  );
}

/** 図面を明細に結び付ける（null で外す）。 */
export function linkQuotationDrawing(
  quotationId: number,
  drawingId: number,
  quotationItemId: number | null,
): Promise<QuotationDrawingListResponse> {
  return api.put<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/${drawingId}/link`,
    { quotation_item_id: quotationItemId },
  );
}

/**
 * 図面の図番で明細を 1 行起こし、その図面を結び付ける。
 *
 * 依頼書に載っていない図面が届いたときの受け皿。突き合わせでも同じことをするが、
 * あちらは「図番が一致する明細が 1 行も無い」と判定できたときだけ。図番が一致
 * する明細はあるが全部埋まっている場合は機械では決められないので、画面から
 * 明示的に呼ぶ。
 */
export function createItemForQuotationDrawing(
  quotationId: number,
  drawingId: number,
): Promise<QuotationDrawingListResponse> {
  return api.post<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/${drawingId}/item`,
    {},
  );
}

export function deleteQuotationDrawing(
  quotationId: number,
  drawingId: number,
): Promise<void> {
  return api.delete<void>(`/quotations/${quotationId}/drawings/${drawingId}`);
}

/**
 * サムネイルがまだ出来ていない図面について、作り直しを仕掛ける。
 *
 * 生成は版の登録の応答を返したあとに走るので、途中でプロセスが落ちると
 * ``pending`` のまま残る。この仕組みより前に登録された版にもサムネイルが無い。
 * その両方の受け皿。応答はすぐ返るので、少し置いてから一覧を取り直す。
 */
export function rebuildQuotationDrawingThumbnails(
  quotationId: number,
  drawingIds?: number[],
): Promise<QuotationDrawingListResponse> {
  return api.post<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/thumbnails/rebuild`,
    { drawing_ids: drawingIds ?? null },
  );
}

/**
 * 図番をまだ読み取れていない図面について、読み直しを仕掛ける。
 *
 * 対象は `pending` / `failed` に加えて `uncertain`（読めたが自信が無い）も。
 * 読み取りは**見積の明細の図番を候補にして**突き合わせるので、依頼の取り込みが
 * 図面より後になった場合、同じ画像でも読み直すと決まることがある。
 * 応答はすぐ返るので、少し置いてから一覧を取り直す。
 */
export function rereadQuotationDrawingNumbers(
  quotationId: number,
  drawingIds?: number[],
): Promise<QuotationDrawingListResponse> {
  return api.post<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/ocr`,
    { drawing_ids: drawingIds ?? null },
  );
}

/** 選んだ図面から明細を起こした結果。 */
export interface QuotationDrawingBulkItemResponse extends QuotationDrawingListResponse {
  created_count: number;
  /** 図番が空で飛ばした図面の数。先に図番を入れる必要がある。 */
  skipped_no_number_count: number;
  /** 既に紐付いていて飛ばした図面の数。 */
  skipped_linked_count: number;
}

/**
 * 選んだ図面それぞれの図番で明細を起こし、紐付ける。
 *
 * 条件を満たさない図面（図番が空、既に紐付き済み）は**飛ばして数を返す**。
 * 1 枚のために選択全部を失敗させると、選び直しの手間だけが増える。
 */
export function createItemsForQuotationDrawings(
  quotationId: number,
  drawingIds: number[],
): Promise<QuotationDrawingBulkItemResponse> {
  return api.post<QuotationDrawingBulkItemResponse>(
    `/quotations/${quotationId}/drawings/items`,
    { drawing_ids: drawingIds },
  );
}

/**
 * 選んだ図面をまとめて削除する。
 *
 * DELETE ではなく POST なのは、消す対象を本文で渡すため（DELETE の本文は経路に
 * よっては落ちる）。1 件でも見つからなければ何も消えない。
 */
export function deleteQuotationDrawings(
  quotationId: number,
  drawingIds: number[],
): Promise<QuotationDrawingListResponse> {
  return api.post<QuotationDrawingListResponse>(
    `/quotations/${quotationId}/drawings/delete`,
    { drawing_ids: drawingIds },
  );
}

/**
 * 見積から見積書 PDF を作って保存する（見積 1 件につき 1 通）。
 *
 * 回答ファイルと同じく**期限付き URL ではなく API が実体を返す**。S3 にも
 * 置かず、呼ばれるたびに組み立てる（見積を直せば次に出した紙に反映される）。
 */
export async function downloadQuotationSheet(quotationId: number): Promise<void> {
  const { blob, filename } = await api.download(`/quotations/${quotationId}/sheet`);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename ?? "見積書.pdf";
    anchor.click();
  } finally {
    // 解放しないと blob がタブを閉じるまで残る。
    URL.revokeObjectURL(url);
  }
}

/**
 * 見積回答ファイルを書き出してダウンロードさせる。
 *
 * 様式は得意先の指定業者で決まる（取り込みと同じ）。いまは京セラの CSV だけ。
 * これまで人が手で表計算に打ち直していた作業の置き換え。
 */
export async function downloadQuotationAnswer(quotationId: number): Promise<void> {
  const { blob, filename } = await api.download(`/quotations/${quotationId}/answer`);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename ?? "見積回答.csv";
    anchor.click();
  } finally {
    // 解放しないと blob がタブを閉じるまで残る。
    URL.revokeObjectURL(url);
  }
}
